// One claim: where it's at, what happened, the stolen items, evidence and
// timeline. Capture only: nothing here talks about liability or settlement.
import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Check, Download } from 'lucide-react';
import { documentUrl, ok } from '@cover/core/api/client.ts';
import type { Claim, DocumentClass } from '@cover/core/api/types.ts';
import { useBrand, whatsappLink } from '@cover/core/brand.tsx';
import { CLAIM_STATUS_LABEL, DOC_CLASS_LABEL, dateTime, money } from '@cover/core/format.ts';
import { cn } from '@cover/core/cn.ts';
import { Badge } from '@cover/core/ui/Badge.tsx';
import { Card, CardBody, CardHeader, Row } from '@cover/core/ui/Card.tsx';
import { Field, Select, Textarea } from '@cover/core/ui/Form.tsx';
import { Load } from '@cover/core/ui/States.tsx';
import { useToast } from '@cover/core/ui/Toast.tsx';
import { api, keys, useClaim, useDocuments } from '../api.ts';
import { UploadButton } from '../components/Upload.tsx';
import { PageTitle } from '../Layout.tsx';
import { useBusinessId } from '../session.tsx';
import { CLAIM_TYPE, claimTone } from './Claims.tsx';

const STEPS: { status: Claim['status']; label: string }[] = [
  { status: 'submitted', label: 'Reported' },
  { status: 'acknowledged', label: 'Received by the claims team' },
  { status: 'in_review', label: 'Being looked at' },
  { status: 'closed', label: 'Closed' },
];

function stepIndex(s: Claim['status']) {
  if (s === 'awaiting_info') return 2;
  return Math.max(0, STEPS.findIndex((x) => x.status === s));
}

const EVIDENCE_KINDS: [DocumentClass, string][] = [
  ['damage_photo', 'Photo of the damage'],
  ['police_report', 'Police report or crime reference'],
  ['receipt', 'Receipt for a stolen item'],
  ['other', 'Something else'],
];

export function ClaimPage() {
  const { claimId = '' } = useParams();
  const claim = useClaim(claimId);
  return (
    <>
      <Link to="/claims" className="mb-3 inline-flex items-center gap-1 text-sm">
        <ArrowLeft className="h-4 w-4" aria-hidden /> All claims
      </Link>
      <Load q={claim}>{(c) => <ClaimView c={c} />}</Load>
    </>
  );
}

function ClaimView({ c }: { c: Claim }) {
  const brand = useBrand();
  const docs = useDocuments();
  const current = stepIndex(c.status);
  const wa = whatsappLink(brand, `About my claim ${c.reference}: `);
  return (
    <div className="space-y-5">
      <PageTitle>{`${CLAIM_TYPE[c.type]} claim`}</PageTitle>
      <div className="-mt-3 flex flex-wrap items-center gap-2">
        <span className="font-mono text-mid">{c.reference}</span>
        <Badge tone={claimTone(c.status)}>{CLAIM_STATUS_LABEL[c.status]}</Badge>
      </div>

      {c.status === 'awaiting_info' && (
        <div role="status" className="rounded-lg bg-warn-bg p-4 text-warn-fg">
          <p className="font-semibold">We need something from you</p>
          <p className="text-sm">Check WhatsApp for what the claims team asked for, then add it below or reply there.</p>
        </div>
      )}

      <Card>
        <CardBody>
          <ol className="space-y-3">
            {STEPS.map((s, i) => {
              const done = i < current || c.status === 'closed';
              const now = i === current && c.status !== 'closed';
              return (
                <li key={s.status} className="flex items-center gap-3">
                  <span className={cn('flex h-7 w-7 shrink-0 items-center justify-center rounded-full border-2 text-sm font-bold', done ? 'border-brand bg-brand text-on-brand' : now ? 'border-brand text-brand' : 'border-line text-muted')} aria-hidden>
                    {done ? <Check className="h-4 w-4" /> : i + 1}
                  </span>
                  <span className={cn(now ? 'font-semibold' : done ? '' : 'text-muted')}>
                    {s.label}
                    {now && <span className="sr-only"> (current step)</span>}
                    {done && <span className="sr-only"> (done)</span>}
                  </span>
                </li>
              );
            })}
          </ol>
          <p className="mt-4 text-sm text-mid">We’ll keep you updated in WhatsApp. {wa && <a href={wa}>Message the claims team</a>}</p>
        </CardBody>
      </Card>

      <Card>
        <CardHeader title="What happened" />
        <CardBody>
          <p className="mb-3 whitespace-pre-line">{c.description}</p>
          <dl className="divide-y divide-line">
            <Row label="When">{dateTime(c.occurredAt)}</Row>
            {c.location && <Row label="Where">{c.location}</Row>}
            {c.policeRef && (
              <Row label="Crime reference">
                <span className="font-mono">{c.policeRef}</span>
              </Row>
            )}
            {c.policyNumber && (
              <Row label="Policy">
                <span className="font-mono">{c.policyNumber}</span>
              </Row>
            )}
          </dl>
        </CardBody>
      </Card>

      {c.stolenAssets && c.stolenAssets.length > 0 && (
        <Card>
          <CardHeader title="Items taken" description={c.claimedValuePence ? `${money(c.claimedValuePence)} in total, from your equipment list` : undefined} />
          <CardBody>
            <ul className="divide-y divide-line">
              {c.stolenAssets.map((a) => (
                <li key={a.id} className="flex justify-between gap-3 py-2">
                  <span>{a.name}</span>
                  <span className="font-medium">{money(a.valuePence)}</span>
                </li>
              ))}
            </ul>
          </CardBody>
        </Card>
      )}

      <Card>
        <CardHeader title="Evidence" />
        <CardBody className="space-y-4">
          {c.evidence?.length ? (
            <ul className="grid grid-cols-2 gap-3 sm:grid-cols-3">
              {c.evidence.map((e) => {
                const doc = docs.data?.find((d) => d.id === e.documentId);
                const isImage = doc?.contentType.startsWith('image/');
                return (
                  <li key={e.documentId}>
                    <a href={documentUrl(e.documentId)} target="_blank" rel="noopener" className="block rounded-md border border-line p-2 text-ink no-underline hover:border-brand/40 hover:no-underline">
                      {isImage ? <img src={documentUrl(e.documentId)} alt={e.note ?? 'Evidence photo'} className="mb-2 aspect-[4/3] w-full rounded object-cover" loading="lazy" /> : null}
                      <span className="block text-sm font-semibold">{e.classification ? DOC_CLASS_LABEL[e.classification as DocumentClass] ?? e.classification : (doc?.filename ?? 'Document')}</span>
                      {e.note && <span className="block text-sm text-mid">{e.note}</span>}
                    </a>
                  </li>
                );
              })}
            </ul>
          ) : (
            <p className="text-mid">No evidence yet.</p>
          )}
          {c.status !== 'closed' && <AddEvidence claim={c} />}
        </CardBody>
      </Card>

      {c.documentId && (
        <a href={documentUrl(c.documentId!)} target="_blank" rel="noopener" className="inline-flex min-h-11 items-center gap-2 font-semibold">
          <Download className="h-4 w-4" aria-hidden /> Claim acknowledgement (PDF)
        </a>
      )}

      <Card>
        <CardHeader title="Timeline" />
        <CardBody>
          <ol className="space-y-3 border-l-2 border-line pl-4">
            {[...c.timeline].reverse().map((t, i) => (
              <li key={`${t.at}-${i}`}>
                <p className="text-sm text-mid">{dateTime(t.at)}</p>
                <p>{t.text}</p>
              </li>
            ))}
          </ol>
        </CardBody>
      </Card>
    </div>
  );
}

function AddEvidence({ claim }: { claim: Claim }) {
  const qc = useQueryClient();
  const b = useBusinessId();
  const toast = useToast();
  const [kind, setKind] = useState<DocumentClass>('damage_photo');
  const [note, setNote] = useState('');
  const attach = useMutation({
    mutationFn: (documentId: string) => ok(api.POST('/claims/{claimId}/evidence', { params: { path: { claimId: claim.id } }, body: { documentId, note: note.trim() || undefined } })),
    onSuccess: async () => {
      setNote('');
      await Promise.all([qc.invalidateQueries({ queryKey: keys.claim(claim.id) }), qc.invalidateQueries({ queryKey: keys.documents(b) })]);
      toast.ok('Added to your claim.');
    },
    onError: (e) => toast.fail(e),
  });
  return (
    <div className="space-y-3 rounded-md bg-canvas p-3">
      <p className="font-semibold">Add evidence</p>
      <Field label="What is it?">
        <Select value={kind} onChange={(e) => setKind(e.target.value as DocumentClass)}>
          {EVIDENCE_KINDS.map(([k, v]) => (
            <option key={k} value={k}>
              {v}
            </option>
          ))}
        </Select>
      </Field>
      <Field label="Note (optional)">
        <Textarea className="min-h-16" maxLength={500} value={note} onChange={(e) => setNote(e.target.value)} />
      </Field>
      <UploadButton label="Choose a photo or file" hint={kind} accept="image/*,application/pdf" busy={attach.isPending} onUploaded={(doc) => attach.mutateAsync(doc.id).then(() => undefined)} />
    </div>
  );
}
